import React from "react";

const isProbablyExternalDomain = (value) => {
  const v = String(value || "").trim();
  if (!v) return false;
  if (/^https?:\/\//i.test(v)) return true;
  if (/^www\./i.test(v)) return true;
  if (/^[^\s/]+\.[^\s/]{2,}([/?#].*)?$/i.test(v)) return true;
  return false;
};

const normalizeHref = (raw) => {
  const v = String(raw || "").trim();
  if (!v) return "";

  const lower = v.toLowerCase();

  // keep mail / tel
  if (lower.startsWith("mailto:")) return v;
  if (lower.startsWith("tel:")) return v;

  // phone number -> WhatsApp
  const phoneLike = /^\+?\d[\d\s-]{6,}$/.test(v);
  if (phoneLike) {
    const digits = v.replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  // wa:972... or whatsapp:972...
  if (lower.startsWith("wa:") || lower.startsWith("whatsapp:")) {
    const digits = v.split(":").slice(1).join(":").replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  // malformed protocol fixes
  if (/^https\/\/:/i.test(v)) {
    return v.replace(/^https\/\/:/i, "https://");
  }

  if (/^http\/\/:/i.test(v)) {
    return v.replace(/^http\/\/:/i, "http://");
  }

  if (/^https:\/(?!\/)/i.test(v)) {
    return v.replace(/^https:\/(?!\/)/i, "https://");
  }

  if (/^http:\/(?!\/)/i.test(v)) {
    return v.replace(/^http:\/(?!\/)/i, "http://");
  }

  // already valid absolute URL
  if (/^https?:\/\//i.test(v)) return v;

  // wa.me direct domain
  if (/^wa\.me\//i.test(v)) return `https://${v}`;

  // www.domain.com
  if (/^www\./i.test(v)) return `https://${v}`;

  // plain domain like example.com or sub.example.com/path
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+([/?#].*)?$/i.test(v)) {
    return `https://${v}`;
  }

  // internal path
  if (v.startsWith("/")) return v;

  // fallback internal slug/path
  return `/${v}`;
};

const goToLink = (raw) => {
  const href = normalizeHref(raw);
  if (!href) return;

  const isExternal =
    /^https?:\/\//i.test(href) ||
    /^mailto:/i.test(href) ||
    /^tel:/i.test(href);

  if (isExternal) {
    window.open(href, "_blank", "noopener,noreferrer");
    return;
  }

  window.location.href = href;
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 135;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const pickAlign = (style, config, fallback = "center") => {
  const v = String(style?.textAlign || style?.alignment || config?.alignment || "")
    .toLowerCase()
    .trim();
  if (v === "left" || v === "center" || v === "right") return v;
  return fallback;
};

const renderActionIcon = (service) => {
  if (service?.buttonIcon) {
    return (
      <img
        src={service.buttonIcon}
        alt=""
        className="w-5 h-5 object-contain"
      />
    );
  }

  return (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3h7v7m0-7L10 14m-4 0H3v7h7" />
    </svg>
  );
};

const ServiceSection1 = ({ config, style }) => {
  const bg = buildBg(style, "bg-white");

  const isRTL = (config?.direction || "ltr") === "rtl";
  const align = pickAlign(style, config, "center");
  const alignMap = { left: "text-left", center: "text-center", right: "text-right" };
  const alignClass = alignMap[align] || "text-center";

  const services = Array.isArray(config.services)
    ? config.services
    : [
        {
          id: 1,
          title: "Consulting Services",
          description: "Expert advice and strategic guidance to help your business grow and overcome challenges.",
          icon: "💡",
          features: ["Strategic Planning", "Market Analysis", "Growth Strategy"],
          link: "example.com",
          buttonLink: "example.com",
          buttonIcon: "",
          badge: "Popular",
        },
        {
          id: 2,
          title: "Professional Training",
          description: "Comprehensive training programs to develop your team's skills and expertise.",
          icon: "📚",
          features: ["Workshops", "Online Courses", "Certification"],
          link: "wa:972501234567",
          buttonLink: "wa:972501234567",
          buttonIcon: "",
        },
        {
          id: 3,
          title: "Support and Maintenance",
          description: "Reliable ongoing support to keep your operations running smoothly.",
          icon: "🔧",
          features: ["Always available", "Regular Updates", "Priority Response"],
          link: "/services/support",
          buttonLink: "/services/support",
          buttonIcon: "",
        },
      ];

  return (
    <section dir={config?.direction || "ltr"} style={bg.style} className={`py-20 px-4 ${bg.className}`}>
      <div className="container mx-auto max-w-7xl">
        <div className={`mb-16 ${alignClass}`}>
          <h2
            style={{ color: style?.titleColor || undefined }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            {config.title || "Our Services"}
          </h2>
          <p
            style={{ color: style?.subtitleColor || undefined }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {config.subtitle || "Comprehensive solutions tailored to your business needs"}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => {
            const actionLink = service?.buttonLink || service?.link || "";

            return (
              <div
                key={service.id}
                style={{
                  backgroundColor: style?.serviceCardBg || undefined,
                  borderColor: style?.serviceCardBorder || undefined,
                }}
                className={`group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 relative ${alignClass}`}
              >
                {service.badge ? (
                  <span
                    style={{
                      backgroundColor: style?.serviceBadgeBg || undefined,
                      color: style?.serviceBadgeTextColor || undefined,
                      borderColor: style?.serviceBadgeBorder || undefined,
                    }}
                    className={`absolute top-4 right-4 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold ${
                      style?.serviceBadgeBorder ? "border" : ""
                    }`}
                  >
                    {service.badge}
                  </span>
                ) : null}

                <div
                  style={{
                    backgroundColor: style?.serviceIconBg || undefined,
                    color: style?.serviceIconColor || undefined,
                  }}
                  className={`text-5xl mb-6 transform group-hover:scale-110 transition-transform duration-300 inline-flex items-center justify-center ${
                    style?.serviceIconBg ? "w-20 h-20 rounded-2xl" : ""
                  }`}
                >
                  {service.icon}
                </div>

                <h3
                  style={{ color: style?.serviceCardTitleColor || undefined }}
                  className="text-2xl font-bold text-gray-900 mb-3"
                >
                  {service.title}
                </h3>

                <p
                  style={{ color: style?.serviceCardDescriptionColor || undefined }}
                  className="text-gray-600 mb-6 leading-relaxed"
                >
                  {service.description}
                </p>

                {Array.isArray(service.features) && service.features.length ? (
                  <ul className="space-y-2 mb-8">
                    {service.features.map((feature, index) => (
                      <li
                        key={index}
                        className={`flex items-center gap-2 text-sm ${
                          isRTL ? "flex-row-reverse justify-end" : ""
                        }`}
                        style={{ color: style?.serviceFeatureTextColor || undefined }}
                      >
                        <svg
                          style={{ color: style?.serviceFeatureIconColor || undefined }}
                          className="w-4 h-4 text-green-500 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {actionLink ? (
                  <button
                    type="button"
                    onClick={() => goToLink(actionLink)}
                    style={{ color: style?.serviceLinkColor || undefined }}
                    className={`inline-flex items-center justify-center rounded-full border border-current w-11 h-11 text-blue-600 font-semibold transition-colors group/btn ${
                      isRTL ? "mr-auto" : "ml-auto"
                    }`}
                    aria-label={service?.title || "Open link"}
                    title={service?.title || "Open link"}
                  >
                    {renderActionIcon(service)}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>

        {config.viewAllLink ? (
          <div className={`mt-12 ${alignClass}`}>
            <button
              type="button"
              onClick={() => goToLink(config.viewAllLink)}
              style={{
                backgroundColor: style?.primaryBtnBg || undefined,
                color: style?.primaryBtnText || undefined,
              }}
              className={`inline-flex items-center gap-2 font-semibold py-4 px-8 rounded-lg transition duration-300 ${
                isRTL ? "flex-row-reverse" : ""
              } ${style?.primaryBtnBg ? "hover:opacity-90" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
            >
              {config.viewAllText || "View All Services"}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ServiceSection1;